// Recovers from a lazily loaded chunk that no longer exists, which happens when
// a deploy changes content hashes while a tab stays open. The first failure for
// an asset hard-reloads onto the route the user asked for. A repeat inside the
// guard window, or hitting the per-tab cap, falls back to the reload prompt, so
// a stale edge cache or a broken build can never loop.
//
// Three entry points share one decision (`recover`):
//   - router.onError, for lazily loaded route components. Always fires and has
//     a reliable `to.fullPath`.
//   - window 'vite:preloadError', for lazy loads outside a navigation. Vite only
//     dispatches it for chunks that have preload deps, so it is a fast path,
//     never the only one.
//   - The recovery bridge, called by plugins/vue-sentry.js from Vue's
//     errorHandler, the unhandledrejection listener, and beforeSend. This is
//     the backstop for imports that produced no event and for in-page failures
//     during a navigation. Sentry suppresses a chunk error only when this path
//     reports that recovery owns it.
//
// The same error object routinely arrives through more than one of these, so
// recover() dedupes on the error identity.
//
// Never call preventDefault() on the Vite event. It makes the import() resolve
// undefined, and router/Index.js reads `resolved.default` inside a guard.
// See https://vite.dev/guide/build#load-error-handling

import * as Sentry from '@sentry/vue';
import i18n from '@/plugins/i18n.js';
import { resetChunkRecoveryBridge, setChunkRecoveryHandler } from '@/router/chunkRecoveryBridge.js';
import { isChunkLoadError } from '@/router/isChunkLoadError.js';
import snackbarService from '@/services/SnackbarService.js';

export { isChunkLoadError };

export const STORAGE_PREFIX = 'edverum:chunk-reload:';
export const RELOAD_GUARD_TTL_MS = 10 * 60 * 1000;
export const RELOAD_MAX_PER_WINDOW = 2;

const RELOAD_IN_FLIGHT_MS = 15 * 1000;
const PENDING_MARKER_MAX_AGE_MS = 60 * 1000;
const COUNT_KEY = `${STORAGE_PREFIX}count`;
const PENDING_KEY = `${STORAGE_PREFIX}pending`;

// Sentry places this string into exception.values[0].value because the plugin
// sets attachStacktrace, so it must not match any isChunkLoadError pattern or
// the beforeSend filter would drop our own signal.
const SENTRY_MESSAGE = 'Stale chunk reload';
const SENTRY_FINGERPRINT = ['stale-chunk-reload'];

let activeToast = null;
let handlerRegistered = false;
let preloadHandlerRegistered = false;
let navigationInFlight = false;
let reloadStartedAt = 0;
let markerConsumed = false;
let handledErrors = new WeakSet();
let preloadListener = null;
let pageshowListener = null;

// sessionStorage is per tab: it survives the reload we issue and bfcache, and a
// second stale tab on the same route is not wrongly demoted to the prompt. A
// thrown access returns `undefined`, distinct from `null` for "absent", so the
// caller can refuse to auto-reload without a persisted guard.
function readJson(key) {
    try {
        const raw = sessionStorage.getItem(key);

        return raw ? JSON.parse(raw) : null;
    } catch (_error) {
        return undefined;
    }
}

function writeJson(key, value) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));

        return true;
    } catch (_error) {
        return false;
    }
}

function removeKey(key) {
    try {
        sessionStorage.removeItem(key);
    } catch (_error) {
        // ignore
    }
}

// Attempt pages can have queued answer saves or instructor-controlled transitions.
// A stale chunk must not initiate a document reload behind those controls.
function isAttemptPage(router) {
    return ['quiz-attempt', 'survey-attempt', 'automated-survey-attempt'].includes(router.currentRoute?.value?.name);
}

function currentUrl() {
    const { pathname, search, hash } = window.location;

    return `${pathname}${search}${hash}`;
}

function stripHash(url) {
    return url.split('#')[0];
}

function hashOf(url) {
    const index = url.indexOf('#');

    return index === -1 ? '' : url.slice(index);
}

// location.assign() to a URL that differs from the current one only by
// fragment, or not at all, is a same-document navigation and does not reload.
// Set the intended fragment first so reload() lands on it.
function hardNavigate(target) {
    if (stripHash(target) !== stripHash(currentUrl())) {
        window.location.assign(target);

        return;
    }

    const hash = hashOf(target);

    if (hash && hash !== window.location.hash) {
        window.location.hash = hash;
    }

    window.location.reload();
}

function messageOf(error) {
    return typeof error === 'string' ? error : (error?.message ?? '');
}

// Chrome, Firefox, and the CSS preload message carry the failing asset URL.
// Safari's "Importing a module script failed." does not, so callers fall back
// to the target route as the key.
function assetUrlFrom(error) {
    const match = messageOf(error).match(/(?:module:|CSS for)\s*(\S+)/i);

    return match?.[1] ?? null;
}

function guardKeyFor(error, target) {
    return `${STORAGE_PREFIX}${assetUrlFrom(error) ?? target}`;
}

function recentlyReloaded(key, now) {
    const at = readJson(key);

    if (at === undefined) {
        return undefined;
    }

    return typeof at === 'number' && now - at < RELOAD_GUARD_TTL_MS;
}

function countInWindow(now) {
    const count = readJson(COUNT_KEY);

    if (count === undefined) {
        return undefined;
    }

    if (!count || typeof count.since !== 'number' || now - count.since >= RELOAD_GUARD_TTL_MS) {
        return { n: 0, since: now };
    }

    return count;
}

function isReloadInFlight(now) {
    return reloadStartedAt > 0 && now - reloadStartedAt < RELOAD_IN_FLIGHT_MS;
}

function report(outcome, extra) {
    try {
        Sentry.addBreadcrumb({ category: 'chunk-load', level: 'info', message: outcome, data: extra });
        Sentry.captureMessage(SENTRY_MESSAGE, {
            level: 'info',
            fingerprint: SENTRY_FINGERPRINT,
            tags: { outcome },
            extra,
        });
    } catch (_error) {
        // Reporting must never block recovery.
    }
}

function promptReload(target, key) {
    activeToast?.close();

    activeToast = snackbarService.open({
        type: 'is-success',
        message: i18n.global.t('new_version'),
        position: 'is-bottom-left',
        actionText: i18n.global.t('reload'),
        indefinite: true,
        onAction: () => {
            // A recurrence after the user's own reload should prompt again
            // rather than spend an automatic one.
            if (key) {
                writeJson(key, Date.now());
            }

            hardNavigate(target);
        },
    });
}

// The marker is written just before we reload and consumed once, on the first
// navigation of the next page life. That is after Sentry has initialized (the
// router module evaluates before the Sentry plugin installs, so reporting at
// registration time would be dropped), and a successful navigation is the
// actual proof that the reload fixed it. `landedAt` is null when that first
// navigation failed on a chunk error again, in which case recover() reports the
// prompt and no 'recovered' claim is made.
function consumePendingMarker(landedAt) {
    if (markerConsumed) {
        return;
    }

    markerConsumed = true;

    const marker = readJson(PENDING_KEY);

    if (!marker) {
        return;
    }

    removeKey(PENDING_KEY);

    const fresh = typeof marker.at === 'number' && Date.now() - marker.at < PENDING_MARKER_MAX_AGE_MS;

    if (fresh && landedAt !== null) {
        report('recovered', { target: marker.target, asset: marker.url, landed: landedAt });
    }
}

/**
 * Decide how to recover from a chunk load failure.
 *
 * @param {{ error: unknown, target: string, source: 'navigation' | 'preload' | 'unhandled', deferReload?: boolean }} options
 * @returns {'reload' | 'prompt' | 'suppressed'}
 */
export function recover({ error, target, source, deferReload = false }) {
    // The Vite event, the rethrown rejection, and Vue's errorHandler can all
    // deliver the same error object. Act on it once.
    const dedupable = typeof error === 'object' && error !== null;

    if (dedupable) {
        if (handledErrors.has(error)) {
            return 'suppressed';
        }

        handledErrors.add(error);
    }

    const now = Date.now();

    if (isReloadInFlight(now)) {
        return 'suppressed';
    }

    const key = guardKeyFor(error, target);
    const seen = recentlyReloaded(key, now);
    const count = countInWindow(now);
    const storageBroken = seen === undefined || count === undefined;
    const extra = { source, target, asset: assetUrlFrom(error), message: messageOf(error) };

    if (deferReload || storageBroken || seen || count.n >= RELOAD_MAX_PER_WINDOW) {
        let reason = 'cap';

        if (deferReload) {
            reason = 'active-attempt';
        } else if (storageBroken) {
            reason = 'storage';
        } else if (seen) {
            reason = 'repeat';
        }

        report('prompted', { ...extra, reason });
        promptReload(target, storageBroken ? null : key);

        return 'prompt';
    }

    const persisted =
        writeJson(key, now) &&
        writeJson(COUNT_KEY, { n: count.n + 1, since: count.since }) &&
        writeJson(PENDING_KEY, { url: extra.asset, target, at: now });

    if (!persisted) {
        report('prompted', { ...extra, reason: 'storage' });
        promptReload(target, null);

        return 'prompt';
    }

    reloadStartedAt = now;
    hardNavigate(target);

    return 'reload';
}

export function registerChunkLoadErrorHandler(router) {
    if (handlerRegistered) {
        return;
    }

    handlerRegistered = true;

    // Backstop for chunk errors that reach Vue's errorHandler, the
    // unhandledrejection listener, or Sentry's beforeSend without having been
    // recovered already. Returning true tells the caller recovery owns it.
    setChunkRecoveryHandler(error => {
        recover({ error, target: currentUrl(), source: 'unhandled', deferReload: isAttemptPage(router) });

        return true;
    });

    router.onError((error, to) => {
        // vue-router does not run afterEach on a failed navigation.
        navigationInFlight = false;

        if (isChunkLoadError(error) && to?.fullPath) {
            consumePendingMarker(null);
            recover({ error, target: to.fullPath, source: 'navigation', deferReload: isAttemptPage(router) });

            return;
        }

        console.error(error);
    });

    router.afterEach(to => {
        navigationInFlight = false;
        activeToast?.close();
        activeToast = null;
        consumePendingMarker(to?.fullPath ?? currentUrl());
    });
}

export function registerPreloadErrorHandler(router) {
    if (preloadHandlerRegistered) {
        return;
    }

    preloadHandlerRegistered = true;

    // Passive: records that a navigation owns any chunk failure until it
    // settles, so router.onError handles it with a reliable destination.
    router.beforeEach(() => {
        navigationInFlight = true;
    });

    // During a navigation this listener stands aside so onError can use the
    // destination. An unrelated in-page import failing at that moment is still
    // caught by the bridge when its rejection reaches the Sentry plugin.
    preloadListener = event => {
        if (!isChunkLoadError(event.payload) || navigationInFlight) {
            return;
        }

        recover({ error: event.payload, target: currentUrl(), source: 'preload', deferReload: isAttemptPage(router) });
    };

    // A bfcache restore brings back the page that issued the reload with its
    // in-flight flag still set; clear it so later failures are not swallowed.
    pageshowListener = event => {
        if (event.persisted) {
            reloadStartedAt = 0;
        }
    };

    window.addEventListener('vite:preloadError', preloadListener);
    window.addEventListener('pageshow', pageshowListener);
}

// Test hook. Removes window listeners and clears module state so specs that
// use vi.resetModules() do not leave stale listeners on the shared window.
export function reset() {
    if (preloadListener) {
        window.removeEventListener('vite:preloadError', preloadListener);
    }

    if (pageshowListener) {
        window.removeEventListener('pageshow', pageshowListener);
    }

    resetChunkRecoveryBridge();
    preloadListener = null;
    pageshowListener = null;
    activeToast?.close();
    activeToast = null;
    handlerRegistered = false;
    preloadHandlerRegistered = false;
    navigationInFlight = false;
    reloadStartedAt = 0;
    markerConsumed = false;
    handledErrors = new WeakSet();
}
